// @ts-strict-ignore
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import Button from 'src/components/buttons/button';
import ContentEditor from 'src/components/textEditorSimple/components/contentEditor';
import { showSignupModal, showModal } from 'src/modules/shared/modal/actions';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import { ModalType } from 'src/constants/modalTypes';
import { showGuidelines } from 'src/modules/community/shared/components/communityGuidelinesModal';
import { EMAIL, PHONE_NUMBER, URL_RE } from 'src/constants/regex';
import { setAlert } from 'src/components/alert/actions';
import { validateReply } from 'src/modules/community/singlePost/components/replies/validateReply';
import { useUnsavedChangesPrompt } from 'src/utils/hooks/useUnsavedChangesPrompt';
import { addHTMLMentionsSimpleEditor, composeHtmlStringFromBB } from 'src/components/textEditor/deserialiseContent';
import { getLocalStorageItem, Key } from 'src/utils/localStorage';
import { ImageElement } from 'src/components/textEditor/customTypes';

type EditorProps = {
    autoFocus?: boolean;
    buttonLabel: string;
    editorValue: string;
    image: ImageElement;
    postOrEdit: (
      editorData: { body: string, mentionedUserIds: number[], image?: ImageElement },
    ) => void;
    respondRef?: MutableRefObject<null | HTMLDivElement>;
  };

const defaultData = { body: '', image: null, mentionedUsers: [] };

export default function ReplyEditorAndroid({ autoFocus = false, buttonLabel, postOrEdit, editorValue, image, respondRef }: EditorProps) {
  const newRef = useRef<HTMLDivElement | null>(null);
  const messageEditorRef = respondRef ?? newRef;
  const dispatch = useDispatch();

  const { currentUser, currentCommunity } = useSelector((state) => state.context);
  const { composeReply, post } = useSelector((state) => state.pages.singlePost);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [data, setData] = useState(defaultData);
  const [isDirty, setIsDirty] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [canValidate, setCanValidate] = useState(false);

  const onChanged = (data) => {
    setData(data);
    if (!canValidate && data.body?.length) {
      setCanValidate(true);
    }
    if (canValidate) {
      validateReply({ text: data.body.trim(), mentionCount: data.mentionedUsers?.length, setIsDirty, setIsSubmitDisabled });
    }
  };

  const trackAddImg = () => trackFootfallEvent(Event.Clicked, { clickType: 'image uploaded', clickMetadata: { Section: Section.PostReply } });

  const canSubmit = (): boolean => {
    if (!currentUser) {
      dispatch(showSignupModal());
      trackFootfallEvent(Event.Clicked, {
        clickType: 'sign-up',
        clickMetadata: {
          element: Element.Reply,
          section: Section.PostReply,
        },
      });
      setIsSubmitDisabled(false);
      return false;
    }
    return true;
  };

  const onSubmit = () => {
    if (canSubmit()) {
      const text = data.body.trim();
      const revalidateErrors = validateReply({ text, mentionCount: data.mentionedUsers?.length, setIsDirty, setIsSubmitDisabled });
      if (revalidateErrors.length) {
        return dispatch(setAlert(revalidateErrors[revalidateErrors.length - 1]));
      }

      const replyData = {
        body: data.body,
        image: data.image,
        mentionedUserIds: data.mentionedUsers?.map(({ userId }) => userId),
      };

      if (!getLocalStorageItem(Key.privacyReminderDismissed) &&
        (EMAIL.test(text) || (PHONE_NUMBER.test(text) && !(URL_RE.test(text))) ||
        !!data.image?.file)) { // if image, only trigger modal when new image is being uploaded
        return dispatch(showModal({
          modalType: ModalType.PrivacyReminder,
          modalProps: {
            onCancel: () => setIsSubmitDisabled(false),
            onAccept: () => postOrEdit(replyData),
            data: { text, image: data?.image?.file ? data.image : null },
          },
        }));
      }
      postOrEdit(replyData);
    }
  };

  useEffect(() => {
    if (!isSubmitDisabled && composeReply.submittedReplyId) {
      setIsReset(true);
      setData(defaultData);
      setCanValidate(false);
      setIsSubmitDisabled(true);
      trackFootfallEvent(Event.Clicked, { clickType: 'completed-reply' });
      setIsDirty(false);
    }
  }, [composeReply.submittedReplyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useUnsavedChangesPrompt({ unsavedChanges: isDirty, onAccept: () => setIsReset(true) });

  const initialValue = editorValue ? composeHtmlStringFromBB({ text: editorValue }) : null;

  const isPrivate = post.isPrivate && !currentUser?.isCommunityMemberOrHUStaff;
  if (isPrivate) return null;

  return (
    <ReplyEditorWrapper onClick={() => messageEditorRef?.current?.focus()}>
      <ContentEditor
        autoFocus={autoFocus}
        initialHtml={initialValue ? addHTMLMentionsSimpleEditor(initialValue, post?.mentionedUsers || []) : ''}
        initialImage={image?.hash && image}
        isReset={isReset}
        placeholder={i18n.t('Add your comment here...')}
        ref={messageEditorRef}
        submitButton={(
          <ReplyButton
            dataTestId="reply-box-reply-button"
            disabled={isSubmitDisabled || composeReply.isLoading}
            text={buttonLabel}
            onClick={() => onSubmit()}
          />
        )}
        onAddImg={trackAddImg}
        onChange={() => {if (isReset) setIsReset(false);}}
        onChanged={onChanged}
        onFocus={() => {
          if (showGuidelines(currentCommunity)) {
            showModal({
              modalType: ModalType.CommunityGuidelines,
              modalProps: { messageEditorRef: messageEditorRef },
            });
          }
        }}
      />
    </ReplyEditorWrapper>
  );
}

const ReplyEditorWrapper = styled.div`
    min-height: auto;
    width: 100%;
    display: inline-block;
    padding: 10px 0 5px;
    border: 1px solid ${({ theme }) => theme.colorGreyLight};
    border-radius: 4px;
    margin-top: 15px;
    @media (${({ theme }) => theme.underScreenMedium}) {
      min-height: auto;
    }
`;

const ReplyButton = styled(Button)`
  float: right;
  margin: 0;
  @media (${({ theme }) => theme.underScreenSmall}) {
    margin-top: 15px;
  }
`;
